import {
    type ComboboxData,
    type ComboboxItem,
    type ComboboxParsedItem,
    type ComboboxParsedItemGroup,
    Loader,
    Select,
    type SelectProps,
} from '@mantine/core';
import type { Except, Merge, SetRequired } from 'type-fest';
import { useCommonInputProps } from '../../../hooks/useCommonInputProps';
import { filterArrayByString, sortBy } from '../../../utils/utilFunctions';
import type {
    Controlled_ValidatedInputProps,
    StringOrNullableString,
} from './types';

const fetchingOption = 'Fetching...';

const isArrayOfComboboxItems = (
    options: Array<ComboboxParsedItem>
): options is Array<ComboboxItem> => options.every((it) => 'label' in it);

const isArrayOfComboboxParsedItemGroups = (
    options: Array<ComboboxParsedItem>
): options is Array<ComboboxParsedItemGroup> =>
    options.every((it) => 'items' in it);

export type Controlled_SelectInputProps<N extends boolean> = Merge<
    Except<
        SetRequired<SelectProps, 'readOnly'>,
        | 'rightSectionWidth'
        | 'rightSection'
        | 'styles'
        | 'w'
        | 'allowDeselect'
        | 'comboboxProps'
    >,
    Controlled_ValidatedInputProps<N, string> & {
        data: ComboboxData | undefined;
    }
>;

export const Controlled_SelectInput = <N extends boolean>({
    value,
    onChange,
    nullable,
    label: _label,
    labelInfo,
    severity,
    helperText,
    endingIcon,
    filter,
    placeholder: _placeholder,
    readOnly,
    nullableInReadOnly,
    searchable,
    data,
    disabled,
    leftSection,
    ...props
}: Controlled_SelectInputProps<N>) => {
    const { rightSectionWidth, rightSection, inputStyles, label, placeholder } =
        useCommonInputProps({
            endingIcon,
            severity,
            helperText,
            nullable,
            onChange,
            value,
            label: _label,
            labelInfo,
            placeholder: _placeholder,
            readOnly,
            nullableInReadOnly,
        });

    const handleChange = onChange
        ? (value: string | null) => onChange(value as StringOrNullableString<N>)
        : undefined;

    return (
        <Select
            {...props}
            data={data ?? [fetchingOption]}
            value={data ? value || null : fetchingOption}
            disabled={!data || disabled}
            leftSection={data ? leftSection : <Loader size="sm" />}
            readOnly={readOnly}
            searchable={searchable ?? false}
            filter={
                filter ??
                (({ options, search }) => {
                    if (!options.length) {
                        return options;
                    }

                    if (isArrayOfComboboxItems(options)) {
                        return filterArrayByString(
                            search,
                            options,
                            ({ label }) => label
                        ).toSorted(sortBy((i) => i.label));
                    }

                    if (isArrayOfComboboxParsedItemGroups(options)) {
                        return options.mapNotNull(({ items, group }) => {
                            const filteredItems = filterArrayByString(
                                search,
                                items,
                                ({ label }) => label
                            ).toSorted(sortBy((i) => i.label));

                            return filteredItems.length
                                ? { group, items: filteredItems }
                                : null;
                        });
                    }

                    return options;
                })
            }
            label={label}
            placeholder={placeholder}
            rightSectionWidth={rightSectionWidth}
            rightSection={rightSection}
            styles={{ input: inputStyles, section: { pointerEvents: 'all' } }}
            onChange={handleChange}
            w={'100%'}
            allowDeselect={false}
            comboboxProps={{ zIndex: 9000 }}
            nothingFoundMessage={'Nothing found...'}
        />
    );
};
